import { useId, useRef, useState, type InputHTMLAttributes } from 'react';
import './Field.css';

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  /**
   * Characters offered as buttons under the field — added 10 September 2026.
   *
   * On a Turkish Q keyboard `@` is AltGr+Q, which a studio owner filling in an
   * e-mail address should not have to know, and which some laptop layouts make
   * genuinely awkward. The product owner could not type it at all. Nothing in
   * the app was blocking the character; the keyboard was, and a keyboard is
   * not something we can fix from here — so the field offers the character
   * instead.
   *
   * Only for fields that actually need an awkward character. A row of buttons
   * under every input would be clutter.
   */
  insertable?: readonly string[];
  /** Required with `insertable`: inserting has to report the new value. */
  onInsert?: (value: string) => void;
}

/**
 * A labelled input.
 *
 * The label is a real <label>, never a placeholder — Document 6 §19. A
 * placeholder disappears as soon as the user types, which is exactly when they
 * need to check what the field was.
 */
export function Field({ label, error, hint, insertable, onInsert, ...rest }: FieldProps) {
  const id = useId();
  /*
   * Every password field can be revealed. Desktop Edge draws its own eye in
   * the field; phones draw nothing, so the only way to check a password typed
   * on a small keyboard was to retype it blind. The browser's icon is hidden
   * in Field.css so a desktop user does not get two.
   */
  const isPassword = rest.type === 'password';
  const [revealed, setRevealed] = useState(false);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const inputRef = useRef<HTMLInputElement>(null);

  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ');

  /**
   * Inserts at the caret, not at the end.
   *
   * Someone who typed the whole address and then noticed the missing `@` puts
   * the caret where it belongs and presses the button; appending would put it
   * after the domain and make the button useless exactly when it is needed.
   */
  function insert(character: string) {
    const input = inputRef.current;
    if (!input || !onInsert) return;

    const current = input.value;
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? current.length;
    const next = current.slice(0, start) + character + current.slice(end);

    onInsert(next);

    // After React re-renders with the new value, put the caret after what was
    // inserted so typing carries on from there rather than from the start.
    // `email` and a few other input types do not support selection at all —
    // `setSelectionRange` throws on them rather than doing nothing — so the
    // whole restore is best-effort: focus lands regardless, the caret only
    // where the browser allows it.
    requestAnimationFrame(() => {
      input.focus();
      const caret = start + character.length;
      try {
        input.setSelectionRange(caret, caret);
      } catch {
        // Unsupported on this input type; focus is the part that mattered.
      }
    });
  }

  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="field__control">
        <input
          {...rest}
          type={isPassword && revealed ? 'text' : rest.type}
          ref={inputRef}
          id={id}
          className={[
            'field__input',
            error ? 'field__input--error' : '',
            isPassword ? 'field__input--has-toggle' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
        />
        {isPassword && (
          <button
            type="button"
            className="field__reveal"
            aria-label={revealed ? 'Şifreyi gizle' : 'Şifreyi göster'}
            aria-pressed={revealed}
            // Keeps the caret in the field: tapping the eye must not close the
            // phone keyboard or move the user's place in what they typed.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => setRevealed((value) => !value)}
          >
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" />
              <circle cx="12" cy="12" r="3" />
              {revealed && <path d="M4 4l16 16" />}
            </svg>
          </button>
        )}
      </div>
      {insertable && insertable.length > 0 && onInsert && (
        <div className="field__insert">
          {insertable.map((character) => (
            <button
              type="button"
              key={character}
              className="field__insert-key"
              // Named, not just shown: a screen reader saying "at" is clearer
              // than one saying the symbol.
              aria-label={`${character} ekle`}
              onClick={() => insert(character)}
            >
              {character}
            </button>
          ))}
        </div>
      )}
      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field__error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}
